import { useFormik } from "formik";
import { convertSumWithLatestCurrencyRate, formatCurrency, formatShortDate, systemCurrencies } from "../../../../Utils/Utils";
import { DisplayErrors } from "../../../../Utils/ErrorHandler";
import useApi from "../../../../Utils/BackendClient";
import { Toolbar } from "primereact/toolbar";

import commonStyle from "../../CommonStyles.module.css";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { numberEditor, priceEditor, textEditor } from "../../../../Utils/TableEditors";
import { Dropdown } from "primereact/dropdown";
import CalendarInput from "../../../UIElements/Inputs/CalendarInput";
import ActNumberWithAutogenerateInput from "../../../UIElements/Inputs/ActNumberWithAutogenerateInput";

const SaleActDetailsContent = ({ ctx, orderData, toast }) => {

    const { createSaleActFromOrder } = useApi();

    const formik = useFormik({
        initialValues: {
            order_id: orderData?.order_id,
            partner_id: orderData?.partner_id,
            act_number_autogenerate: true,
            products: orderData?.products ?? [],
            services: orderData?.services ?? [],
            act_number: "",
            act_date: new Date(),
            pay_due_date: new Date(),
            discount: []
        },
        validate: values => {
            const errors = {};

            return errors;
        },
        onSubmit: async (values) => {

            const payload = {
                ...values,
                act_date: formatShortDate(values.act_date),
                pay_due_date: formatShortDate(values.pay_due_date),
                products: values.products.map(product => product.id),
                services: values.services.map(service => service.id)
            };

            const response = await createSaleActFromOrder(payload);

            if (response.status === 201) {
                toast.current.show({ severity: "success", summary: "Успіх", detail: `Партнерський акт № ${response.data.id} створено}`, life: 3000 });
            }
            else {
                DisplayErrors(response.error, toast);
            }
        }
    });

    const actToolbarLeftTemplate = () => {
        return <h3>Cтворення акту продажу товару</h3>;
    };

    const actToolbarRightTemplate = () => {
        return (
            <div className="flex flex-wrap gap-2">
                <Button label="Зберегти" severity="success" className={commonStyle.addButton} type="submit" />
            </div>
        );
    };

    const setTableHeader = (tablename) => {
        return (
            <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                <span className="text-xl text-900 font-bold">{tablename}</span>
            </div>
        );
    }

    const addDiscountHandler = () => {
        const discount = [
            ...formik.values.discount,
            { currency: "UAH", amount: 0 }
        ]

        formik.setFieldValue('discount', discount);
    }

    const handleParameterChange = (index, value, parameter, array) => {
        const obj = [...formik.values[array]];
        obj[index][parameter] = value;
        formik.setFieldValue(array, obj);
    };

    const handleCellEditComplete = (e, arrayName) => {
        const rowIndex = e.index ?? e.rowIndex;

        const updated = [...formik.values[arrayName]];
        updated[rowIndex] = {
            ...updated[rowIndex],
            [e.field]: e.newValue
        };

        formik.setFieldValue(arrayName, updated);
    };

    const deleteObjFromArray = (index, array) => {

        const newArray = formik.values[array];
        newArray.splice(index);
        formik.setFieldValue(array, newArray);
    }

    return (
        <form onSubmit={formik.handleSubmit}>
            <Toolbar className="mb-4" start={actToolbarLeftTemplate} end={actToolbarRightTemplate} />
            <div className="grid">
                <div className={`${commonStyle.leftMenu} col-3`}>
                    <CalendarInput formik={formik} paramName={"pay_due_date"} label={"Очікувана дата оплати"} />
                    <ActNumberWithAutogenerateInput formik={formik} />
                    <div className={`${commonStyle.formInput} mt-4`}>
                        <div className="p-inputgroup">
                            <span className="p-inputgroup-addon w-full flex justify-content-start">Знижка</span>
                            <Button
                                icon="pi pi-plus"
                                className={commonStyle.addButton}
                                severity="success"
                                tooltip="Додати знижку"
                                type="button"
                                onClick={() => addDiscountHandler()}
                            />
                        </div>
                    </div>
                    {formik.values.discount.map((currencyAmount, index) => (
                        <div key={index} className={`${commonStyle.formInput} ${commonStyle.marginBottom}`}>
                            <div className="p-inputgroup flex-1">
                                <span className={`${commonStyle.currencySpan} p-inputgroup-addon`}>
                                    <Dropdown
                                        className={`${commonStyle.currencyDropDown} w-full`}
                                        value={currencyAmount.currency}
                                        onChange={e => handleParameterChange(index, e.value, "currency", "discount")}
                                        options={systemCurrencies}
                                    />
                                </span>
                                <InputText
                                    onChange={e => handleParameterChange(index, e.target.value, "amount", "discount")}
                                    name="amount"
                                    value={currencyAmount.amount}
                                    className={formik.errors.price && formik.touched.price ? 'p-invalid' : ''}
                                />
                                <Button
                                    icon="pi pi-trash"
                                    className={commonStyle.deleteButton}
                                    severity="danger"
                                    tooltip="Видалити знижку"
                                    type="button"
                                    onClick={() => deleteObjFromArray(index, "discount")}
                                />
                            </div>
                        </div>))
                    }
                </div>
                <div className="col-9">
                    {formik.values.products.length > 0 && <DataTable value={formik.values.products} header={setTableHeader("Товари")} editMode="cell">
                        <Column field="quantity" header="Кількість" editor={(options) => numberEditor(options)} onCellEditComplete={(e) => handleCellEditComplete(e, "products")} style={{ width: '5%' }} />
                        <Column field="description" header="Товар" editor={(options) => textEditor(options)} onCellEditComplete={(e) => handleCellEditComplete(e, "products")} style={{ width: '40%' }} />
                        <Column field="identifier" header="Ідентифікатор" editor={(options) => numberEditor(options, "identifier", "products", formik)}
                            body={(rowdata => rowdata.accounting_type === "INDIVIDUAL" ? rowdata.identifier : "-")} style={{ width: '25%' }} />
                        <Column field="price" header="Ціна за одиницю" editor={(options) => priceEditor(options, "price", "products", formik)} body={(rowdata) => formatCurrency(rowdata.price, rowdata.currency)} style={{ width: '15%' }} />
                        <Column field="priceUAH" header="Ціна за одиницю, грн" body={(rowdata) => formatCurrency(convertSumWithLatestCurrencyRate(rowdata.currency, rowdata.price), "UAH")} style={{ width: '15%' }} />
                    </DataTable>}
                    {formik.values.services.length > 0 && <DataTable value={formik.values.services} header={setTableHeader("Послуги")} editMode="cell">
                        <Column field="description" header="Послуга" style={{ width: '70%' }} />
                        <Column field="price" header="Ціна за одиницю" editor={(options) => priceEditor(options, "price", "products", formik)} body={(rowdata) => formatCurrency(rowdata.price, rowdata.currency)} style={{ width: '15%' }} />
                        <Column field="priceUAH" header="Ціна за одиницю, грн" body={(rowdata) => formatCurrency(convertSumWithLatestCurrencyRate(rowdata.currency, rowdata.price), "UAH")} style={{ width: '15%' }} />
                    </DataTable>}
                </div>
            </div>
        </form>
    );
}

export default SaleActDetailsContent;